/* eslint-disable @typescript-eslint/indent */
import { createApi } from '@reduxjs/toolkit/query/react';
import { TServerResponse } from 'app/types/server-response';
import { transportAPIBase } from 'app/utilities/env';
import getBaseQueryWithLogout from 'app/utilities/getBaseQuery';
import { TTrip } from 'features/trips/types/trips';

import { TFleet } from '../types/fleet';
import {
  ReRouteDTO,
  RescheduleDTO,
  TRouteSchedule,
  TRouteScheduleDTO,
  TSaveTripResponse,
  TTransportCharterDTO,
  TTransportCharterRequestDTO,
  TTransportCharterResponse,
  TTripDTO,
} from '../types/route-schedule';
import { Terminal } from '../types/terminals';

export const transportAPISlice = createApi({
  reducerPath: 'transportApi',
  baseQuery: getBaseQueryWithLogout(`${transportAPIBase}`),
  tagTypes: ['Trips'],
  endpoints: (build) => ({
    getTerminals: build.query<Terminal[], void>({
      query: () => '/terminals',
      transformResponse: (response: TServerResponse<Terminal[]>) =>
        response.data,
    }),
    getRouteSchedules: build.mutation<TRouteSchedule[], TRouteScheduleDTO>({
      query: (body) => ({
        url: '/route-schedule/check',
        method: 'POST',
        body,
      }),
      transformResponse: (response: TServerResponse<TRouteSchedule[]>) =>
        response.data,
    }),

    getCharterSchedules: build.mutation<TFleet[], TTransportCharterDTO>({
      query: (body) => ({
        url: '/charter/check',
        method: 'POST',
        body,
      }),
      transformResponse: (response: TServerResponse<TFleet[]>) => response.data,
    }),

    requestCharter: build.mutation<
      TTransportCharterResponse,
      TTransportCharterRequestDTO
    >({
      query: (body) => ({
        url: '/charter',
        method: 'POST',
        body,
      }),
      transformResponse: (
        response: TServerResponse<TTransportCharterResponse>
      ) => response.data,
    }),

    negotiateCharter: build.mutation<
      TTransportCharterResponse,
      TTransportCharterRequestDTO
    >({
      query: (body) => ({
        url: '/charter/negotiate',
        method: 'POST',
        body,
      }),
    }),

    saveTrip: build.mutation<TSaveTripResponse, TTripDTO>({
      query: (body) => ({
        url: '/trips',
        method: 'POST',
        body,
      }),
      transformResponse: (res: TServerResponse<TSaveTripResponse>) => res.data,
      invalidatesTags: ['Trips'],
    }),

    getUserTrips: build.query<
      Record<'upcomingTrips' | 'pastTrips', TTrip[]>,
      number
    >({
      query: (id) => `/trips/${id}`,
      transformResponse: (
        res: TServerResponse<Record<'upcomingTrips' | 'pastTrips', TTrip[]>>
      ) => res.data,
      providesTags: ['Trips'],
    }),

    reRouteTrip: build.mutation<TTrip, ReRouteDTO>({
      query: (params) => ({
        url: `/trips/save-reroute/${params.tripId}`,
        body: {
          destinationId: params.destinationId,
          originId: params.originId,
          departureDate: params.departureDate,
        },
        method: 'POST',
      }),
      transformResponse: (res: TServerResponse<TTrip>) => res.data,
      invalidatesTags: ['Trips'],
    }),

    checkReroute: build.mutation<unknown, ReRouteDTO>({
      query: ({ tripId, ...body }) => ({
        url: `/trips/reroute/${tripId}`,
        method: 'POST',
        body,
      }),
      transformResponse: (res: TServerResponse<unknown>) => res.data,
    }),

    reScheduleTrip: build.mutation<unknown, RescheduleDTO>({
      query: (params) => ({
        url: `/trips/reschedule/${params.tripId}`,
        body: {
          departureDate: params.departureDate,
        },
        method: 'POST',
      }),
      invalidatesTags: ['Trips'],
    }),
  }),
});

export const {
  useGetTerminalsQuery,
  useGetRouteSchedulesMutation,
  useGetCharterSchedulesMutation,
  useRequestCharterMutation,
  useLazyGetTerminalsQuery,
  useSaveTripMutation,
  useGetUserTripsQuery,
  useLazyGetUserTripsQuery,
  useNegotiateCharterMutation,
  useReRouteTripMutation,
  useCheckRerouteMutation,
  useReScheduleTripMutation,
} = transportAPISlice;
